"use client";
/**
 * Loyalty HUB (Block 5, design surfaces 1.1 + 1.4 minus the money-wallet tab).
 *
 * Sections, top to bottom:
 *   - Tier hero: tier badge, points balance, progress to next tier, grace
 *     banner when a downgrade is pending (ANNUAL_SPEND basis).
 *   - Streaks widget (design 1.7) — hidden when streaks are disabled.
 *   - Tier benefits list, resolved from POST /v1/loyalty/benefits/resolve.
 *   - Points redemption options (existing rewards) + activity feed.
 *   - No-tier state for brand-new customers (tier ladder explainer).
 *   - Wallet-pass entry row linking to /wallet-pass.
 *
 * Wallet / Loyalty-Pay (money) is intentionally OUT of scope (regulated).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Badge, Button, Card, SectionHeader, TopBar } from "@/components/ui";
import { Price } from "@/components/Price";
import {
  TierBadge,
  IconChip,
  benefitMeta,
  ProgressBar,
  SignedOutGate,
} from "@/components/loyalty/LoyaltyBits";
import { StreakWidget } from "@/components/loyalty/StreakWidget";
import { useLoyalty, useLoyaltyTransactions } from "@/hooks/useLoyalty";
import {
  useBenefits,
  useChallenges,
  useTierLadder,
  deriveStreakView,
  localizedField,
} from "@/hooks/useLoyaltyExpansion";
import { useAuth } from "@/store/auth";
import type { TierLadderEntry } from "@/api/services";

export function LoyaltyWalletScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { isLoggedIn } = useAuth();

  const { loading, error, balance, rewards, refresh, isNotFound } = useLoyalty();
  const { benefits, tierName: resolvedTierName, tierGrace } = useBenefits(lang);
  const { tiers: tierLadder } = useTierLadder();
  const { streaksEnabled, streaks } = useChallenges();
  const { transactions } = useLoyaltyTransactions();

  const [signInOpen, setSignInOpen] = useState(false);

  // Prefer the loyalty balance's tier name; fall back to the resolved tier.
  const tier = balance?.tier ?? resolvedTierName ?? null;

  const progress = useMemo(() => {
    if (!balance?.nextTier || balance.nextTier.pointsAway <= 0) return 1;
    const total = balance.points + balance.nextTier.pointsAway;
    if (total <= 0) return 0;
    return Math.max(0.04, Math.min(1, balance.points / total));
  }, [balance]);

  const streakView = useMemo(
    () => (streaksEnabled ? deriveStreakView(streaks) : null),
    [streaksEnabled, streaks],
  );

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.loyalty.title")} onBack={() => navigate("/profile")} />
        <SignedOutGate
          icon={<Icons.spark size={32} />}
          title={t("v2.loyalty.signin_title")}
          desc={t("v2.loyalty.signin_desc")}
          cta={t("v2.profile.signin_phone")}
        />
      </div>
    );
  }

  // ── No-tier / not-enrolled state (new customer) — design 1.1 "no tier" ──
  if (isNotFound || (!loading && !balance)) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.loyalty.title")} onBack={() => navigate("/profile")} />
        <div style={{ padding: "16px 16px 0", display: "flex", flexDirection: "column", gap: 16 }}>
          <Card
            padding={22}
            style={{
              background: "linear-gradient(160deg, var(--loy-hero-from) 0%, var(--loy-hero-to) 100%)",
              color: "#fff",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                margin: "0 auto",
                background: "rgba(255,255,255,0.08)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icons.spark size={28} />
            </div>
            <div className="display" style={{ fontSize: 20, marginTop: 12 }}>
              {t("v2.loyalty_hub.start_title")}
            </div>
            <div style={{ opacity: 0.75, fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>
              {t("v2.loyalty_hub.start_desc")}
            </div>
            <Button
              full
              onClick={() => navigate("/")}
              style={{ marginTop: 16, background: "#fff", color: "var(--ink-1)" }}
            >
              {t("v2.loyalty_hub.back_to_menu")}
            </Button>
          </Card>

          {/* Tier ladder — driven by the tenant's OWN tiers (GET /v1/loyalty/tiers).
              When the tenant defines no tiers (or the endpoint isn't reachable),
              fall back to neutral copy rather than inventing tier names. */}
          <div>
            <div
              className="display"
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ink-2)",
                marginBottom: 10,
              }}
            >
              {t("v2.loyalty_hub.tier_ladder")}
            </div>
            {tierLadder.length > 0 ? (
              <Card padding={0} style={{ overflow: "hidden" }}>
                {tierLadder.map((tier, i) => {
                  const name = localizedField(tier.name, lang) ?? tier.tierId;
                  const blurb =
                    localizedField(tier.benefitsDescription, lang) ||
                    tier.benefits
                      .map((b) => benefitMeta(b, t).title)
                      .filter(Boolean)
                      .slice(0, 3)
                      .join(" · ") ||
                    t("v2.loyalty_hub.tier_generic_blurb");
                  return (
                    <div
                      key={tier.tierId || i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: 14,
                        borderTop: i === 0 ? 0 : "1px solid var(--border)",
                      }}
                    >
                      <TierBadge tier={name} />
                      <div style={{ flex: 1, fontSize: 13, color: "var(--ink-2)" }}>
                        {blurb}
                      </div>
                    </div>
                  );
                })}
              </Card>
            ) : (
              <Card padding={16}>
                <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.45 }}>
                  {t("v2.loyalty_hub.tier_ladder_generic")}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  const graceTierName = localizedField(tierGrace?.pendingTierName, lang);

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={t("v2.loyalty.title")}
        onBack={() => navigate("/profile")}
        right={
          <Button variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
            {t("v2.common.retry")}
          </Button>
        }
      />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {/* ── Tier hero ── */}
        <Card
          padding={18}
          style={{
            overflow: "hidden",
            position: "relative",
            background: "linear-gradient(160deg, var(--loy-hero-from) 0%, var(--loy-hero-to) 100%)",
            color: "#fff",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              insetInlineEnd: -30,
              top: -30,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "radial-gradient(closest-side, rgba(244,214,138,0.35), transparent 70%)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div className="eyebrow" style={{ opacity: 0.7, color: "#fff" }}>
                {t("v2.loyalty_hub.loyalty_tier")}
              </div>
              <div style={{ marginTop: 6 }}>
                {tier ? (
                  <TierBadge tier={tier} />
                ) : (
                  <span className="display" style={{ fontSize: 16 }}>
                    {t("v2.loyalty_hub.member")}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            {loading && !balance ? (
              <div
                style={{
                  width: 140,
                  height: 42,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.12)",
                }}
              />
            ) : (
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span className="display num" style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.03em" }}>
                  {balance?.programType === "cashback" ? (
                    <Price amount={balance?.cashback ?? 0} />
                  ) : (
                    (balance?.points ?? 0).toLocaleString()
                  )}
                </span>
                {balance?.programType !== "cashback" && (
                  <span style={{ opacity: 0.7, fontSize: 14, fontWeight: 600 }}>
                    {t("v2.profile.points")}
                  </span>
                )}
              </div>
            )}

            {balance?.nextTier && balance.nextTier.pointsAway > 0 && (
              <div style={{ marginTop: 12 }}>
                <ProgressBar value={progress} dark />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 8,
                    fontSize: 12,
                    opacity: 0.85,
                  }}
                >
                  <span>{tier ?? ""}</span>
                  <span>
                    <span className="num" style={{ fontWeight: 700 }}>
                      {balance.nextTier.pointsAway.toLocaleString()}
                    </span>{" "}
                    {t("v2.loyalty_hub.points_to_next", { tier: balance.nextTier.name })}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ── Grace banner (downgrade pending) ── */}
        {tierGrace && (
          <Card
            padding={14}
            style={{
              background: "var(--bonus-bg)",
              border: "1px solid #f4d99c",
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <IconChip tone="amber">
              <Icons.bell size={20} />
            </IconChip>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--bonus)" }}>
                {t("v2.loyalty_hub.grace_title", { tier: tier ?? "" })}
              </div>
              <div style={{ fontSize: 12.5, color: "#7a4815", marginTop: 4, lineHeight: 1.45 }}>
                {t("v2.loyalty_hub.grace_desc", { tier: graceTierName ?? "" })}
              </div>
              {tierGrace.graceUntil && (
                <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                  <span className="badge amber">
                    <Icons.cal size={12} />
                    {t("v2.loyalty_hub.grace_until", {
                      date: new Date(tierGrace.graceUntil).toLocaleDateString(lang),
                    })}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* ── Streaks (design 1.7) — hidden when disabled ── */}
        {streakView && <StreakWidget view={streakView} />}

        {/* ── Tier benefits ── */}
        {benefits.length > 0 && (
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div className="display" style={{ fontSize: 16 }}>
                {tier
                  ? t("v2.loyalty_hub.your_tier_benefits", { tier })
                  : t("v2.loyalty_hub.your_benefits")}
              </div>
              <span className="badge gold">
                {t("v2.loyalty_hub.n_active", { count: benefits.length })}
              </span>
            </div>
            <Card padding={0}>
              {benefits.map((b, idx) => {
                const meta = benefitMeta(b, t);
                const Ic = meta.icon;
                return (
                  <div
                    key={`${b.type}-${idx}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: 14,
                      borderTop: idx === 0 ? 0 : "1px solid var(--border)",
                    }}
                  >
                    <IconChip tone={meta.tone}>
                      <Ic size={20} />
                    </IconChip>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{meta.title}</div>
                      {meta.sub && (
                        <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                          {meta.sub}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          </section>
        )}

        {error && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.loyalty.couldnt_load")}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#7f1d1d" }}>{error.message}</div>
          </Card>
        )}

        {/* ── Redemption options (points rewards) ── */}
        {rewards.length > 0 && (
          <section>
            <SectionHeader title={t("v2.loyalty.rewards")} sub={t("v2.loyalty.rewards_sub")} />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {rewards.map((reward) => {
                const affordable = (balance?.points ?? 0) >= reward.pointsCost;
                return (
                  <Card key={reward.id} padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <IconChip tone={affordable ? "primary" : "surface"}>
                      <Icons.ticket size={18} />
                    </IconChip>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                        {reward.name}
                      </div>
                      {reward.description && (
                        <div style={{ marginTop: 2, fontSize: 12, color: "var(--ink-3)" }}>
                          {reward.description}
                        </div>
                      )}
                    </div>
                    <Badge tone={affordable ? "primary" : "neutral"}>
                      {reward.pointsCost} {t("v2.profile.pts")}
                    </Badge>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Activity feed ── */}
        {transactions.length > 0 && (
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <div className="display" style={{ fontSize: 16 }}>
                {t("v2.loyalty_hub.recent_activity")}
              </div>
              <button
                onClick={() => navigate("/points-history")}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--ink-2)",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {t("v2.loyalty_hub.see_all")}
                <Icons.chevron size={14} />
              </button>
            </div>
            <Card padding={0}>
              {transactions.slice(0, 5).map((tx, i) => {
                const change = tx.pointsChange || tx.cashbackChange;
                const credit = change >= 0;
                return (
                  <div
                    key={tx.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 14,
                      borderTop: i === 0 ? 0 : "1px solid var(--border)",
                    }}
                  >
                    <IconChip tone={credit ? "primary" : "surface"} size={36}>
                      {credit ? <Icons.plus size={16} /> : <Icons.bag size={16} />}
                    </IconChip>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{tx.description}</div>
                      {tx.createdAt && (
                        <div className="subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
                          {new Date(tx.createdAt).toLocaleDateString(lang)}
                        </div>
                      )}
                    </div>
                    <div
                      className="num"
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: credit ? "var(--primary-700)" : "var(--ink-1)",
                      }}
                    >
                      {credit ? "+ " : "− "}
                      {Math.abs(change)}
                    </div>
                  </div>
                );
              })}
            </Card>
          </section>
        )}

        {/* ── Wallet-pass entry row (design 1.5) ── */}
        <Card
          padding={14}
          onClick={() => navigate("/wallet-pass")}
          style={{ display: "flex", alignItems: "center", gap: 12 }}
        >
          <IconChip tone="dark">
            <Icons.wallet size={20} />
          </IconChip>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {t("v2.loyalty_hub.wallet_pass_row_title")}
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {t("v2.loyalty_hub.wallet_pass_row_sub")}
            </div>
          </div>
          <Icons.chevron size={16} style={{ color: "var(--ink-3)" }} />
        </Card>
      </div>
    </div>
  );
}
